import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from "react";
import Dexie, { EntityTable } from "dexie";
import {
  IAppState,
  IMessageThreadDraft,
  IFileInTransit,
} from "./index-db-models";

type DexieDB = Dexie & {
  appState: EntityTable<IAppState, "id">;
  draft: EntityTable<IMessageThreadDraft, "id">;
  file: EntityTable<IFileInTransit, "id">;
};

interface IContextProps {
  db: DexieDB;
  deleteAll: () => Promise<void>;
}

const DB_NAME = "synco";

const createDb = (): DexieDB => {
  const _db = new Dexie(DB_NAME) as DexieDB;
  _db.version(1).stores({
    appState: "++id",
    draft: "++id",
    file: "++id, draftId, fileId",
  });
  return _db;
};

const db = createDb();

const IndexDbContext = createContext<IContextProps | null>(null);

export const IndexDbProvider = ({ children }: PropsWithChildren) => {
  const deleteAll = useCallback(async () => {
    // clear the tables rather than close/delete/reopen the database: while the
    // db is closed, any concurrently-running query (e.g. a mounted
    // useLiveQuery) rejects with DatabaseClosedError
    await Promise.all(db.tables.map((table) => table.clear()));
  }, []);

  const value = useMemo<IContextProps>(() => ({ db, deleteAll }), [deleteAll]);

  return (
    <IndexDbContext.Provider value={value}>{children}</IndexDbContext.Provider>
  );
};

export const useIndexDb = (): IContextProps => {
  const ctx = useContext(IndexDbContext);
  if (!ctx) {
    throw new Error("useIndexDb must be used within an IndexDbProvider");
  }
  return ctx;
};
